"use client";

import {
  CollapsibleNavGroup,
  matchesPath,
} from "@punchup/ui-common/components/common";
import { ComputedNavItem, buildNavTree } from "./navigation/utils";

import { BASE_NAV_ITEM_KEYS } from "../../data/navigation";
import { Drawer, DrawerContent } from "@punchup/ui-common/shadcn/ui/drawer";
import { VenuePageEvents, trackEvent } from "@punchup/analytics";

import { ArrowUpRight } from "lucide-react";
import Link from "next/link";
import { cn } from "@punchup/utils/cn";
import { useMemo } from "react";

interface VenuePageDrawerMenuProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  navItems: ComputedNavItem[];
  pathname: string;
  venueSlug: string;
}

export default function VenuePageDrawerMenu({
  isOpen,
  onOpenChange,
  navItems,
  pathname,
  venueSlug,
}: VenuePageDrawerMenuProps) {
  const navTree = useMemo(() => buildNavTree(navItems), [navItems]);
  const commonLinkClasses = cn(
    "flex flex-col justify-center items-start border-b border-[#C7C8C9] -mb-px px-5 bg-transparent transition-colors duration-200 ease-in-out leading-tight",
    "border-none"
  );

  const activeLinkClasses =
    "border-b-2 border-primary-blue text-primary-blue font-semibold";

  const handleLinkClick = (item: ComputedNavItem) => {
    switch (item.itemType) {
      case "base":
        trackEvent(VenuePageEvents.CLICKED_INTERNAL_LINK, {
          venueSlug,
          label: item.label,
          href: item.href,
        });
        break;
      case "custom":
        trackEvent(VenuePageEvents.CLICKED_CUSTOM_INTERNAL_LINK, {
          venueSlug,
          label: item.label,
          href: item.href,
          itemKey: item.itemKey,
        });
        break;
      case "external":
        trackEvent(VenuePageEvents.CLICKED_EXTERNAL_LINK, {
          venueSlug,
          label: item.label,
          url: item.href,
        });
        break;
    }
    // Close drawer for internal links only
    if (item.type !== "external") {
      onOpenChange(false);
    }
  };

  const isLinkActive = (item: ComputedNavItem): boolean => {
    if (item.type === "external") {
      return false;
    }
    // The home entry is reachable under either url base, so match both venue
    // roots as candidate hrefs instead of string-matching its label.
    const isHome =
      item.itemType === "base" && item.itemKey === BASE_NAV_ITEM_KEYS.HOME;
    const candidateHrefs = isHome
      ? [`/v/${venueSlug}`, `/venue/${venueSlug}`]
      : [item.href];
    return candidateHrefs.some((href) => matchesPath(href, pathname, false));
  };

  const renderLink = (item: ComputedNavItem, isChild = false) => {
    const isActive = isLinkActive(item);
    const isExternal = item.type === "external";
    return (
      <Link
        key={`${item.itemType}-${item.itemKey}`}
        className={cn(
          commonLinkClasses,
          isActive && activeLinkClasses,
          isChild && "pl-8"
        )}
        onClick={() => handleLinkClick(item)}
        href={item.href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
      >
        <span className="flex flex-row items-center gap-x-1 pb-1.5 pt-3">
          {item.label}
          {isExternal && <ArrowUpRight className="w-5 h-5" />}
        </span>
      </Link>
    );
  };

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent
        className="z-[99] font-regular flex items-center gap-x-4"
        data-testid="drawer-menu"
      >
        <div className="h-[75dvh] w-full pt-2 pb-8 overflow-y-scroll">
          <nav className="flex flex-col gap-1.5 w-full text-xl text-secondary-blue font-medium">
            {navTree.map((node) => {
              if (node.itemType === "group") {
                return (
                  <CollapsibleNavGroup
                    key={`group-${node.itemKey}`}
                    label={node.label}
                    triggerClassName={commonLinkClasses}
                  >
                    {(node.children ?? []).map((child) =>
                      renderLink(child, true)
                    )}
                  </CollapsibleNavGroup>
                );
              }
              return renderLink(node);
            })}

            {/* Go to Punchup link - always shown */}
            <div className="h-px my-1 bg-neutral-200" />
            <Link
              className={cn(commonLinkClasses)}
              href="https://punchup.live/"
              onClick={() => onOpenChange(false)}
            >
              <span className="flex flex-row items-center gap-x-1 pb-1.5 pt-3">
                Go to Punchup
                <ArrowUpRight className="w-5 h-5" />
              </span>
            </Link>
          </nav>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
